import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useCarouselApi } from '@elfsight/lib-carousel';
import { useDebounceCallback } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../../../meta';
import { ItemData } from '../../items/ItemData';
import { getImageAspectRatio } from '../../useImagesAspectRatios';
import { MAX_HEIGHT_UPDATES, MIN_CAROUSEL_HEIGHT } from '../utils';

export const CarouselItem = ({ data, id, carouselImageRatio }) => {
  const settings = useSettings();
  const { carouselItemSpacing } = settings;
  const [originalRatio, setOriginalRatio] = useState(null);

  const carouselApi = useCarouselApi();
  const carouselItemRef = useRef(null);
  const updateHeightCountRef = useRef(0);

  const aspectRatio = useMemo(() => {
    if (!carouselImageRatio && typeof originalRatio === 'number') {
      return 1 / originalRatio;
    }

    return carouselImageRatio;
  }, [carouselImageRatio, originalRatio]);

  const retryUpdateHeight = useDebounceCallback(() => {
    if (!carouselItemRef.current) {
      return;
    }

    const carousel = carouselItemRef.current.closest('.swiper-wrapper');
    if (carousel?.clientHeight > MIN_CAROUSEL_HEIGHT) {
      updateHeightCountRef.current = 0;

      return;
    }

    if (updateHeightCountRef.current < MAX_HEIGHT_UPDATES) {
      updateHeight();
    }
  });

  const updateHeight = useCallback(() => {
    requestAnimationFrame(() => {
      if (carouselApi) {
        carouselApi.updateAutoHeight();
      }

      if (updateHeightCountRef.current < MAX_HEIGHT_UPDATES) {
        retryUpdateHeight();
        updateHeightCountRef.current += 1;
      }
    });
  }, [carouselApi, retryUpdateHeight]);

  useEffect(() => {
    if (!data?.image || !!carouselImageRatio) {
      return;
    }
    const setRatio = async () => {
      const { image } = data;
      const { url = '' } = image;
      const imageInfo = await getImageAspectRatio(url);

      setOriginalRatio(imageInfo?.aspectRatio || 1.5);
    };

    setRatio();
  }, [data, carouselImageRatio]);

  useEffect(() => {
    updateHeight();
  }, [updateHeight]);

  return (
    <Item
      ref={carouselItemRef}
      _aspectRatio={aspectRatio}
      _carouselItemSpacing={carouselItemSpacing}
    >
      <ItemContent>
        <ItemData data={data} id={id} />
      </ItemContent>
    </Item>
  );
};

const Item = styled.div`
  user-select: none;
  position: relative;

  & .${meta.stylePrefix}-item-data-imageContainer {
    padding-top: ${({ _aspectRatio = 0.6666 }) => _aspectRatio * 100}%;
  }

  & .${meta.stylePrefix}-item-data-watermarkLogoWrapper img {
    height: auto;
    position: relative;
  }

  &:last-child {
    margin-bottom: 0;
  }

  &:not(:first-child) {
    margin-top: ${({ _carouselItemSpacing = 20 }) => _carouselItemSpacing}px;
  }
`;

const ItemContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;

  & img {
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }
`;
